import React, { useEffect, useRef, useState } from "react";

export interface RecordingEvent {
  id: string;
  batchNo: string;
  courseName: string;
  type: "paused" | "stopped" | "started";
  timestamp: number;
}

interface Props {
  events: RecordingEvent[];
  onDismiss: (id: string) => void;
}

export function RecordingToastContainer({ events, onDismiss }: Props) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {events.map((evt) => (
        <RecordingToast key={evt.id} event={evt} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

const TOAST_CONFIG = {
  started: {
    bg: "bg-emerald-50 border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    textColor: "text-emerald-800",
    dismissColor: "text-emerald-500",
    emoji: "🟢",
    label: "Recording Started",
  },
  paused: {
    bg: "bg-amber-50 border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    textColor: "text-amber-800",
    dismissColor: "text-amber-500",
    emoji: "🔴",
    label: "Recording Paused",
  },
  stopped: {
    bg: "bg-red-50 border-red-200",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    textColor: "text-red-800",
    dismissColor: "text-red-500",
    emoji: "⛔",
    label: "Recording Stopped",
  },
} as const;

function RecordingToast({
  event,
  onDismiss,
}: {
  event: RecordingEvent;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Fade in
    const show = setTimeout(() => setVisible(true), 20);
    // Auto-dismiss after 6 seconds
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(event.id), 300);
    }, 6000);
    return () => {
      clearTimeout(show);
      clearTimeout(timerRef.current);
    };
  }, [event.id, onDismiss]);

  const config = TOAST_CONFIG[event.type];

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border min-w-[280px] max-w-sm transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      } ${config.bg}`}
    >
      {/* Icon */}
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.iconBg}`}
      >
        {event.type === "started" ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-4 h-4 ${config.iconColor}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-4 h-4 ${config.iconColor}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            {event.type === "stopped" ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9 10h6v4H9z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            )}
          </svg>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${config.textColor}`}>
          {config.emoji} {config.label}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 truncate">
          Batch {event.batchNo} · {event.courseName}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={() => {
          clearTimeout(timerRef.current);
          setVisible(false);
          setTimeout(() => onDismiss(event.id), 300);
        }}
        className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-black/10 transition-colors ${config.dismissColor}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}